import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { adminLogin, resetStatus } from "../../../slice/adminUser/adminUser"; 

/**
 * Tamigo Admin Portal — Sign in
 * Same layout idea as the reference (centered card, banner, brand mark,
 * two fields, primary button, secured footer) but restyled with its own
 * palette, type system and a hand-built brass "diya" signature mark
 * instead of the source logo image.
 */

export default function TamigoLogin() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // ---- Redux state from adminUserSlice ----
  const { loading, error, token, role } = useSelector((state) => state.adminUser);

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [localError, setLocalError] = useState("");

  // Reset any stale success/error state when the login screen mounts
  useEffect(() => {
    dispatch(resetStatus());
  }, [dispatch]);

  // Redirect once we actually have a token in the store (i.e. login succeeded)
  useEffect(() => {
    if (token) {
      navigate("/admin/dashboard"); // ⬅ change to wherever your admin app should land
    }
  }, [token, role, navigate]);

  function handleSubmit(e) {
    e.preventDefault();

    if (!username.trim() || !password.trim()) {
      setLocalError("Enter your username and password to continue.");
      return;
    }
    setLocalError("");

    dispatch(adminLogin({ username: username.trim(), password }));
  }

  const displayError = localError || error;

  return (
    <div className="tmg-page">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,500;0,600;1,500&family=Manrope:wght@400;500;600;700&family=IBM+Plex+Mono:wght@400;500&display=swap');

        .tmg-page {
      --night-0: #492F07;
    --night-1: #674723;
    --night-2: #614728;
          --clay: #C1622D;
          --clay-light: #E08E52;
          --clay-dark: #8F491E;
          --cream: #FBF3E7;
          --cream-2: #F3E6D3;
          --ink: #2B1D14;
          --muted: #8A7967;
          --sage: #4F7965;
          --danger: #B5442E;

          min-height: 100vh;
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 48px 20px;
          box-sizing: border-box;
          background:
            radial-gradient(circle at 18% 20%, rgba(224,142,82,0.14), transparent 42%),
            radial-gradient(circle at 82% 78%, rgba(79,121,101,0.16), transparent 45%),
            linear-gradient(160deg, var(--night-0), var(--night-1) 55%, var(--night-2));
          font-family: 'Manrope', sans-serif;
        }

        .tmg-page *, .tmg-page *::before, .tmg-page *::after {
          box-sizing: border-box;
        }

        .tmg-card {
          width: 100%;
          max-width: 408px;
          border-radius: 22px;
          overflow: hidden;
          box-shadow:
            0 30px 60px -20px rgba(0,0,0,0.55),
            0 0 0 1px rgba(255,255,255,0.04);
        }

        .tmg-banner {
            background: linear-gradient(
  135deg,
  #6B421F 0%,
  #8A571F 45%,
  #C47A08 100%
);
    height: 60px;
    display: flex
;
    justify-content: center;
        }

    
        .tmg-banner-label {
          position: relative;
          font-family: 'IBM Plex Mono', monospace;
          font-size: 11px;
          letter-spacing: 0.16em;
          text-transform: uppercase;
          color: rgba(255,247,238,0.92);
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .tmg-banner-label .tmg-dot {
          width: 5px;
          height: 5px;
          border-radius: 50%;
          background: #7ED6B0;
          box-shadow: 0 0 0 3px rgba(126,214,176,0.25);
        }

    
        .tmg-body {
          padding: 0 32px 32px;
          position: relative;
          background:#FDF6EC;
        }

    

        .tmg-flame {
          transform-origin: 50% 100%;
          animation: tmg-flicker 2.4s ease-in-out infinite;
        }

        @keyframes tmg-flicker {
          0%, 100% { transform: scaleY(1) rotate(0deg); }
          25% { transform: scaleY(1.06) rotate(-1.5deg); }
          50% { transform: scaleY(0.96) rotate(1deg); }
          75% { transform: scaleY(1.03) rotate(-0.5deg); }
        }

        .tmg-wordmark {
          text-align: center;
          font-family: 'Cormorant Garamond', serif;
          font-weight: 600;
          font-size: 34px;
          color: var(--ink);
          letter-spacing: 0.01em;
          margin: 4px 0 0;
        }

        .tmg-tagline {
          text-align: center;
          font-family: 'IBM Plex Mono', monospace;
          font-size: 10.5px;
          letter-spacing: 0.2em;
          text-transform: uppercase;
          color: var(--muted);
          margin: 4px 0 26px;
        }

        .tmg-divider {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 22px;
        }
        .tmg-divider span {
          flex: 1;
          height: 1px;
          background: linear-gradient(90deg, transparent, var(--cream-2) 20%, var(--cream-2) 80%, transparent);
        }

        .tmg-heading {
          font-family: 'Cormorant Garamond', serif;
          font-weight: 600;
       
          font-size: 24px;
          color: var(--ink);
          margin: 0 0 22px;
        }

        .tmg-field {
          margin-bottom: 16px;
        }

        .tmg-label {
           display: block;
    font-size: 11.5px;
    font-weight: 600;
    color: #83644D;
    margin-bottom: 6px;
    letter-spacing: 0.01em;
        }

        .tmg-input-wrap {
          position: relative;
          display: flex;
          align-items: center;
        }

        .tmg-input {
          width: 100%;
          font-family: 'Manrope', sans-serif;
          font-size: 14.5px;
          color: var(--ink);
          background: #FFFFFF;
          border: 1.5px solid var(--cream-2);
          border-radius: 12px;
          padding: 12px 14px;
          outline: none;
          transition: border-color 0.15s ease, box-shadow 0.15s ease;
        }

        .tmg-input::placeholder { color: #B7A794; }

        .tmg-input:focus {
          border-color: var(--sage);
          box-shadow: 0 0 0 3px rgba(79,121,101,0.16);
        }

        .tmg-input[type="password"], .tmg-input.tmg-pw {
          padding-right: 44px;
        }

        .tmg-eye {
          position: absolute;
          right: 10px;
          background: none;
          border: none;
          cursor: pointer;
          padding: 6px;
          color: var(--muted);
          display: flex;
          align-items: center;
          border-radius: 8px;
        }
        .tmg-eye:hover { color: var(--ink); background: var(--cream-2); }
        .tmg-eye:focus-visible {
          outline: 2px solid var(--sage);
          outline-offset: 1px;
        }

        .tmg-error {
          font-size: 12.5px;
          color: var(--danger);
          margin: -6px 0 14px;
        }

        .tmg-submit {
          width: 100%;
          margin-top: 6px;
          padding: 13px 14px;
          border: none;
          border-radius: 12px;
          background: #E2CDA8;
          color: #6c6c6c;
          font-family: 'Manrope', sans-serif;
          font-weight: 700;
          font-size: 14.5px;
          letter-spacing: 0.02em;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          transition: transform 0.12s ease, box-shadow 0.15s ease, filter 0.15s ease;
          box-shadow: 0 10px 22px -10px rgba(143,73,30,0.7);
        }
        .tmg-submit:hover { filter: brightness(1.06); }
        .tmg-submit:active { transform: scale(0.98); }
        .tmg-submit:focus-visible {
          outline: 2px solid var(--sage);
          outline-offset: 2px;
        }
        .tmg-submit:disabled { cursor: progress; filter: brightness(0.95); }

        .tmg-spinner {
          width: 15px;
          height: 15px;
          border-radius: 50%;
          border: 2px solid rgba(255,246,236,0.35);
          border-top-color: #FFF6EC;
          animation: tmg-spin 0.7s linear infinite;
        }
        @keyframes tmg-spin { to { transform: rotate(360deg); } }

        .tmg-footer {
          margin-top: 22px;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          font-family: 'IBM Plex Mono', monospace;
          font-size: 11px;
          letter-spacing: 0.03em;
          color: var(--muted);
        }

        @media (prefers-reduced-motion: reduce) {
          .tmg-flame { animation: none; }
          .tmg-spinner { animation-duration: 1.4s; }
        }

        @media (max-width: 420px) {
          .tmg-body { padding: 0 22px 26px; }
          .tmg-banner { padding: 18px 20px 30px; }
        }

        
      `}</style>

      <div className="tmg-card">
        <div className="tmg-banner">
          <div className="tmg-banner-label">
            <span className="tmg-dot" aria-hidden="true" />
            Admin Portal · Singapore
          </div>
       
        </div>

        <div className="tmg-body">
         

          <p className="tmg-wordmark">Tamigo</p>
          <p className="tmg-tagline">Tradition to your doorstep</p>

          <div className="tmg-divider"><span /></div>

          <h1 className="tmg-heading">Sign in to continue</h1>

          <form onSubmit={handleSubmit} noValidate>
            <div className="tmg-field">
              <label className="tmg-label" htmlFor="tmg-username">User name</label>
              <div className="tmg-input-wrap">
                <input
                  id="tmg-username"
                  type="text"
                  className="tmg-input"
                  placeholder="Enter your username"
                  autoComplete="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                />
              </div>
            </div>

            <div className="tmg-field">
              <label className="tmg-label" htmlFor="tmg-password">Password</label>
              <div className="tmg-input-wrap">
                <input
                  id="tmg-password"
                  type={showPassword ? "text" : "password"}
                  className="tmg-input tmg-pw"
                  placeholder="Enter your password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <button
                  type="button"
                  className="tmg-eye"
                  onClick={() => setShowPassword((s) => !s)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                      <path d="M3 3l18 18M10.6 10.6a3 3 0 0 0 4.24 4.24M6.6 6.6C4.2 8.2 2.5 10.5 2 12c1.6 4.6 6 8 10 8 1.7 0 3.3-.4 4.7-1.2M9.9 4.2A10.6 10.6 0 0 1 12 4c4 0 8.4 3.4 10 8-.5 1.4-1.3 2.9-2.4 4.2" />
                    </svg>
                  ) : (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                      <path d="M2 12c1.6-4.6 6-8 10-8s8.4 3.4 10 8c-1.6 4.6-6 8-10 8s-8.4-3.4-10-8Z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            {displayError && <p className="tmg-error">{displayError}</p>}

            <button className="tmg-submit" type="submit" disabled={loading}>
              {loading ? (
                <>
                  <span className="tmg-spinner" aria-hidden="true" />
                  Signing in…
                </>
              ) : (
                "Sign in"
              )}
            </button>
          </form>

          <div className="tmg-footer">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <rect x="5" y="11" width="14" height="9" rx="2" />
              <path d="M8 11V7a4 4 0 0 1 8 0v4" />
            </svg>
            Secured · v2.1.0 · Singapore
          </div>
        </div>
      </div>
    </div>
  );
}